import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, RefreshCw, Key, Eye, EyeOff, Shield, Settings, Unlock } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Module definitions with Turkish names
const MODULE_DEFINITIONS = {
  dashboard: { name: 'Dashboard', icon: '📊' },
  port_calls: { name: 'Liman Çağrıları', icon: '🚢' },
  proformas: { name: 'Proformalar', icon: '📋' },
  invoices: { name: 'Faturalar', icon: '🧾' },
  payments: { name: 'Ödemeler', icon: '💳' },
  clients: { name: 'Müşteriler', icon: '👥' },
  vessels: { name: 'Gemiler', icon: '⚓' },
  motor_launches: { name: 'Motor Seferleri', icon: '🚤' },
  anchorage: { name: 'Demirsahası Takip', icon: '⚡' },
  documents: { name: 'Belgeler', icon: '📄' },
  reports: { name: 'Raporlar', icon: '📈' },
  settings: { name: 'Ayarlar', icon: '⚙️' },
  admin_panel: { name: 'Yönetim Paneli', icon: '🔐' }
};

const DEFAULT_PERMISSIONS = {
  dashboard: true,
  port_calls: true,
  proformas: true,
  invoices: true,
  payments: true,
  clients: true,
  vessels: true,
  motor_launches: true,
  anchorage: true,
  documents: true,
  reports: true,
  settings: false,
  admin_panel: false
};

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer'
  });
  const [permissionsData, setPermissionsData] = useState({...DEFAULT_PERMISSIONS});
  const [passwordData, setPasswordData] = useState({
    new_password: ''
  });
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [includeDeleted]);

  const loadUsers = async () => {
    try {
      const response = await api.get(`/users?include_deleted=${includeDeleted}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'viewer'
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleOpenPermissions = (user) => {
    setSelectedUser(user);
    setPermissionsData(user.module_permissions || {...DEFAULT_PERMISSIONS});
    setShowPermissionsModal(true);
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        module_permissions: permissionsData
      });
      toast.success('Modül izinleri güncellendi');
      setShowPermissionsModal(false);
      loadUsers();
    } catch (error) {
      toast.error('İzinler güncellenemedi');
    } finally {
      setSavingPermissions(false);
    }
  };

  const toggleAllPermissions = (value) => {
    const newPermissions = {};
    Object.keys(MODULE_DEFINITIONS).forEach(key => {
      newPermissions[key] = value;
    });
    setPermissionsData(newPermissions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await api.post('/users', formData);
        toast.success('Kullanıcı başarıyla oluşturuldu');
      } else {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        await api.put(`/users/${selectedUser.id}`, updateData);
        toast.success('Kullanıcı başarıyla güncellendi');
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Kullanıcı başarıyla silindi');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı silinemedi');
    }
  };

  const handleRestore = async (userId) => {
    try {
      await api.put(`/users/${userId}/restore`);
      toast.success('Kullanıcı başarıyla geri yüklendi');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı geri yüklenemedi');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: '' });
    setShowPasswordModal(true);
  };

  const handleUnblockUser = async (user) => {
    if (!window.confirm(`${user.name} kullanıcısının blokesini kaldırmak istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      await api.put(`/users/${user.id}/unblock`);
      toast.success(`${user.name} kullanıcısının blokesi kaldırıldı`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bloke kaldırılamadı');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUser.id}/reset-password`, passwordData);
      toast.success('Şifre başarıyla sıfırlandı');
      setShowPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre sıfırlanamadı');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'badge-approved',
      operations: 'badge-sent',
      accounting: 'badge-draft',
      viewer: 'badge'
    };
    return colors[role] || 'badge';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Yönetici',
      operations: 'Operasyon',
      accounting: 'Muhasebe',
      viewer: 'İzleyici'
    };
    return labels[role] || role;
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title">Yönetim Paneli - Kullanıcı Yönetimi</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            <span style={{ fontSize: '0.875rem' }}>Silinmiş Kullanıcıları Göster</span>
          </label>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Plus size={18} />
            Kullanıcı Ekle
          </button>
        </div>
      </div>

      <div style={{ padding: '2rem' }}>
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Users size={24} />
              <h3 className="card-title">Sistem Kullanıcıları</h3>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><Users /></div>
                <h3 className="empty-state-title">Kullanıcı bulunamadı</h3>
                <p className="empty-state-text">Başlamak için ilk kullanıcınızı oluşturun</p>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Ad</th>
                      <th>E-posta</th>
                      <th>Rol</th>
                      <th>Durum</th>
                      <th>Son Giriş</th>
                      <th>Oluşturulma</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} style={{ opacity: user.active ? 1 : 0.6 }}>
                        <td><strong>{user.name}</strong></td>
                        <td>{user.email}</td>
                        <td>
                          <span className={getRoleBadgeColor(user.role)}>
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td>
                          <span className={user.blocked ? 'badge-cancelled' : (user.active ? 'badge-approved' : 'badge-draft')}>
                            {user.blocked ? '🔒 Bloke' : (user.active ? 'Aktif' : 'Silinmiş')}
                          </span>
                        </td>
                        <td>
                          {user.last_login 
                            ? new Date(user.last_login).toLocaleString('tr-TR') 
                            : 'Hiçbir zaman'}
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {user.blocked ? (
                              <button
                                className="action-btn"
                                onClick={() => handleUnblockUser(user)}
                                title="Blokeyi Kaldır"
                                style={{ color: '#f59e0b' }}
                              >
                                <Unlock size={18} />
                              </button>
                            ) : user.active ? (
                              <>
                                <button
                                  className="action-btn"
                                  onClick={() => handleEdit(user)}
                                  title="Kullanıcıyı Düzenle"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button
                                  className="action-btn"
                                  onClick={() => handleOpenPermissions(user)}
                                  title="Modül İzinleri"
                                  style={{ color: '#7c3aed' }}
                                >
                                  <Shield size={18} />
                                </button>
                                <button
                                  className="action-btn"
                                  onClick={() => handleResetPassword(user)}
                                  title="Şifreyi Sıfırla"
                                >
                                  <Key size={18} />
                                </button>
                                <button
                                  className="action-btn action-btn-danger"
                                  onClick={() => handleDelete(user.id)}
                                  title="Kullanıcıyı Sil"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            ) : (
                              <button
                                className="action-btn"
                                onClick={() => handleRestore(user.id)}
                                title="Kullanıcıyı Geri Yükle"
                              >
                                <RefreshCw size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {modalMode === 'create' ? 'Yeni Kullanıcı Ekle' : 'Kullanıcıyı Düzenle'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Ad *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">E-posta *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                {modalMode === 'create' && (
                  <div className="form-group">
                    <label className="form-label">Şifre *</label>
                    <input
                      type="password"
                      className="form-input"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Rol *</label>
                  <select
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <option value="viewer">İzleyici (Sadece Okuma)</option>
                    <option value="operations">Operasyon</option>
                    <option value="accounting">Muhasebe</option>
                    <option value="admin">Yönetici</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalMode === 'create' ? 'Kullanıcı Oluştur' : 'Kullanıcıyı Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Şifre Sıfırla - {selectedUser?.name}</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Yeni Şifre *</label>
                  <input
                    type="password"
                    className="form-input"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ new_password: e.target.value })}
                    required
                    minLength={6}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Minimum 6 karakter
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Şifreyi Sıfırla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Module Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Shield size={24} style={{ marginRight: '0.5rem', color: '#7c3aed' }} />
                Modül İzinleri - {selectedUser.name}
              </h2>
              <button className="modal-close" onClick={() => setShowPermissionsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                background: '#f5f3ff', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                color: '#5b21b6'
              }}>
                <strong>Not:</strong> Seçili modüller kullanıcı tarafından görülebilir ve erişilebilir olacaktır.
              </div>

              {/* Quick Actions */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <button
                  type="button"
                  onClick={() => toggleAllPermissions(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#dcfce7',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  Tümünü Seç
                </button>
                <button
                  type="button"
                  onClick={() => toggleAllPermissions(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  Tümünü Kaldır
                </button>
              </div>

              {/* Module List */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.75rem' 
              }}>
                {Object.entries(MODULE_DEFINITIONS).map(([key, module]) => (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: permissionsData[key] ? '#f0fdf4' : '#f8fafc',
                      border: permissionsData[key] ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={permissionsData[key] || false}
                      onChange={(e) => setPermissionsData({
                        ...permissionsData,
                        [key]: e.target.checked
                      })}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#22c55e'
                      }}
                    />
                    <span style={{ fontSize: '1.25rem' }}>{module.icon}</span>
                    <span style={{ 
                      fontWeight: permissionsData[key] ? 600 : 400,
                      color: permissionsData[key] ? '#166534' : '#64748b'
                    }}>
                      {module.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowPermissionsModal(false)}
              >
                İptal
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                style={{ background: '#7c3aed' }}
              >
                {savingPermissions ? 'Kaydediliyor...' : 'İzinleri Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
